import React, { useState } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { KeyRound, RefreshCw, Copy, Eye, EyeOff } from 'lucide-react';

const input =
  'w-full rounded-lg border border-gray-300 px-3 py-2 font-mono text-xs focus:border-primary-600 focus:outline-none focus:ring-4 focus:ring-primary-100';

/** URL-safe random string, generated in the browser via the Web Crypto API. */
const randomToken = (bytes) => {
  const a = new Uint8Array(bytes);
  (window.crypto || window.msCrypto).getRandomValues(a);
  return Array.from(a, (b) => b.toString(16).padStart(2, '0')).join('');
};

/**
 * Credentials the bank presents when calling us.
 *
 * The same pair serves both schemes: OAuth2 client_credentials, which is what the
 * Family Bank OB_COLLECTIONS spec requires, and HTTP Basic, kept for providers
 * that only support it. Secrets are never sent back to the browser — the API
 * returns them masked, and submitting a masked value leaves the stored one alone.
 */
const InboundCredentials = ({ integration, onSaved }) => {
  const cfg = integration.config || {};
  const hasSecret = Boolean(cfg.inbound_client_secret || cfg.inbound_password);

  const [clientId, setClientId] = useState(cfg.inbound_client_id || cfg.inbound_username || '');
  const [secret, setSecret] = useState('');
  const [show, setShow] = useState(false);
  const [saving, setSaving] = useState(false);

  const generate = () => {
    setClientId(`fb_${randomToken(6)}`);
    setSecret(randomToken(16));
    setShow(true);
    toast('Generated - copy the secret before saving, it is not shown again', { icon: 'key' });
  };

  const copy = (v, what) => {
    navigator.clipboard?.writeText(v)
      .then(() => toast.success(`${what} copied`))
      .catch(() => toast.error('Copy failed - select it manually'));
  };

  const save = async (e) => {
    e.preventDefault();
    if (!clientId.trim()) { toast.error('Client ID is required'); return; }
    if (!hasSecret && !secret.trim()) { toast.error('Set a client secret'); return; }
    setSaving(true);
    try {
      await axios.put(`/api/integrations/${integration.id}`, {
        config: {
          inbound_client_id: clientId.trim(),
          inbound_username: clientId.trim(),
          ...(secret.trim()
            ? { inbound_client_secret: secret.trim(), inbound_password: secret.trim() }
            : {}),
        },
      });
      toast.success('Credentials saved');
      setSecret('');
      if (onSaved) onSaved();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not save credentials');
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={save} className="mt-4 rounded-lg border border-gray-200 bg-gray-50/60 p-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="flex items-center gap-1.5 text-sm font-medium text-gray-900">
            <KeyRound className="h-4 w-4 text-gray-500" /> Inbound credentials
          </p>
          <p className="mt-0.5 text-xs text-gray-500">
            What the bank presents when calling us - OAuth2 client_credentials, or HTTP Basic.
          </p>
        </div>
        <button type="button" onClick={generate}
          className="inline-flex shrink-0 items-center gap-1 rounded-lg border border-gray-300 bg-white px-2.5 py-1.5 text-xs font-medium text-gray-700 hover:border-primary-600 hover:text-primary-700">
          <RefreshCw className="h-3.5 w-3.5" /> Generate
        </button>
      </div>

      <div className="mt-3 grid gap-3 sm:grid-cols-2">
        <div>
          <label htmlFor={`cid-${integration.id}`} className="mb-1 block text-xs font-medium text-gray-700">
            Client ID / username
          </label>
          <div className="flex gap-1.5">
            <input id={`cid-${integration.id}`} className={input} value={clientId}
              onChange={(e) => setClientId(e.target.value)} autoComplete="off" spellCheck="false" />
            {clientId && (
              <button type="button" onClick={() => copy(clientId, 'Client ID')} title="Copy"
                className="shrink-0 rounded-lg border border-gray-300 bg-white px-2 text-gray-500 hover:text-gray-900">
                <Copy className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
        </div>

        <div>
          <label htmlFor={`csec-${integration.id}`} className="mb-1 block text-xs font-medium text-gray-700">
            Client secret / password
          </label>
          <div className="flex gap-1.5">
            <input id={`csec-${integration.id}`} className={input}
              type={show ? 'text' : 'password'} value={secret} autoComplete="new-password" spellCheck="false"
              placeholder={hasSecret ? 'Set - leave blank to keep' : 'Not set'}
              onChange={(e) => setSecret(e.target.value)} />
            <button type="button" onClick={() => setShow((v) => !v)} title={show ? 'Hide' : 'Show'}
              className="shrink-0 rounded-lg border border-gray-300 bg-white px-2 text-gray-500 hover:text-gray-900">
              {show ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
            </button>
            {secret && (
              <button type="button" onClick={() => copy(secret, 'Secret')} title="Copy"
                className="shrink-0 rounded-lg border border-gray-300 bg-white px-2 text-gray-500 hover:text-gray-900">
                <Copy className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
        <p className="text-xs text-gray-500">
          {hasSecret
            ? 'A secret is stored. It is never sent back to this page - enter a new one to replace it.'
            : 'No secret stored yet. Token requests will be refused until one is set.'}
        </p>
        <button type="submit" disabled={saving}
          className="rounded-lg bg-primary-700 px-3 py-2 text-sm font-medium text-white hover:bg-primary-800 disabled:opacity-60">
          {saving ? 'Saving...' : 'Save credentials'}
        </button>
      </div>
    </form>
  );
};

export default InboundCredentials;
